// ============================================================================
// THE DATA CONTRACT — types.ts
//
// The front end never imports supabase-js for reads. It imports THIS interface,
// which has two implementations:
//
//   demo.ts    a deterministic in-memory dataset          (default, zero config)
//   remote.ts  fetch() against the Worker                 (once accounts exist)
//   index.ts   picks one and wraps every method in a TanStack Query hook
//
// THE RULE, from commit one and never optional: adding a method means touching
// types.ts + demo.ts + remote.ts + a hook. Demo mode must keep working forever.
// A missing demo implementation is a failed unit test, not a runtime surprise.
// ============================================================================

import type {
  Announcement,
  Assignment,
  CaptureToken,
  Connector,
  ConnectionsScenario,
  Course,
  ClassOccurrence,
  DataStatus,
  DesktopWorkerStatus,
  Flashcard,
  ISODate,
  InterestSave,
  InterestTopic,
  JournalEntry,
  LearningItem,
  LearningResource,
  LearningStreak,
  LearningTrack,
  Lecture,
  LectureSummary,
  PracticeQuestion,
  SyllabusDocument,
  SyllabusExtract,
  SyncResult,
  SyncStatusRow,
  Task,
  Term,
  Transcript,
  TranscriptSegment,
  VoiceMemo,
} from '@app/shared'

// Calendar's read vocabulary. Its own import line rather than more names above: parallel lanes
// edit this file and a one-identifier edit inside a shared import list is a merge conflict for
// no benefit — the convention the chat/capture import below already set.
import type {
  CalendarBlockState,
  CalendarBlock,
  CalendarRange,
  CalendarWritableSource,
} from '@app/shared'

export interface DateRange {
  from: ISODate
  to: ISODate
}

/**
 * The app's notion of "now". Demo mode returns a FIXED clock so every render and
 * every future screenshot is reproducible; remote mode returns the real one.
 * Nothing in the UI is allowed to call Date.now() directly — it would make demo
 * mode drift and quietly break visual baselines.
 */
export interface Clock {
  date: ISODate
  /** Minutes past local midnight. */
  minutesIntoDay: number
}

export interface JournalEntryInput {
  date: ISODate
  text?: string
  mood?: number | null
  energy?: number | null
  tags?: string[]
}

export interface TaskInput {
  title: string
  due?: ISODate | null
  list?: string
  reminderAt?: string | null
  /** Minutes. Omit for a task with no estimate — nothing infers one from the title. */
  estimateMin?: number | null
  /**
   * An RFC 5545 rule from the Worker's closed vocabulary (FREQ=DAILY|WEEKLY|MONTHLY, optional
   * INTERVAL, BYDAY on WEEKLY) — see apps/worker/src/lib/recurrence.ts. Omit or null for a
   * one-off. Set from the Reminders composer since 2026-09-14; the field always existed on the
   * row, nothing in the UI wrote it before.
   */
  rrule?: string | null
}

/**
 * Every field optional, and NULL IS A MEANING: `{ due: null }` genuinely clears the date,
 * `{}` changes nothing. The same absent/null distinction the Worker's PATCH makes
 * (apps/worker/src/routes/tasks.ts), because they are two sides of one endpoint.
 *
 * COMPLETION IS NOT HERE. It has `toggleTask`, because completing a repeating task also
 * materialises the next occurrence — one act, one call, not a flag some caller could flip.
 */
export interface TaskPatch {
  title?: string
  due?: ISODate | null
  list?: string
  reminderAt?: string | null
  estimateMin?: number | null
  /** Set, change or (`null`) stop a repeat. Same vocabulary as `TaskInput.rrule`. */
  rrule?: string | null
}

// ── calendar (docs/design/calendar-brief.md; mockups 11a week, 11b day) ──────
//
// The READ shape lives in @app/shared (`CalendarRange`, `CalendarBlock`, `CalendarDueItem`),
// because the Worker assembles it and the browser draws it and the two have to agree about
// what a week is. Only the two WRITE shapes are here, and only LifeOS-owned blocks have them:
// there is deliberately no input type that can name `source: 'canvas'`, so "Canvas events are
// read-only" (brief rule 1) is enforced by the compiler before it is enforced by anything else.

export interface CalendarBlockInput {
  title: string
  source: CalendarWritableSource
  startsAt: ISOTimestamp
  endsAt: ISOTimestamp
  location?: string | null
  /** Set when the block was made by dragging a task onto the grid. */
  taskId?: string | null
  /**
   * Defaults to `accepted`. A block written as `suggested` is a PROPOSAL: visible, labelled,
   * and counted by nothing until something accepts it (brief rule 2).
   */
  state?: CalendarBlockState
  /** Makes the write idempotent on `(owner, source, externalId)`. */
  externalId?: string | null
  /**
   * WHEN TO BE TOLD ABOUT THIS BLOCK — minutes BEFORE `startsAt`, Google-Calendar style. `0` is
   * "at start". Whole numbers, 0…40320 (four weeks), at most 20 of them; the Worker refuses
   * anything else with a sentence.
   *
   * ABSENT MEANS NONE, and a new block gets none unless the owner adds one. There is no default
   * offset anywhere in this feature: picking one for him would be the app inventing a number
   * (CLAUDE.md → product identity).
   */
  reminders?: number[]
}

/** Every field optional; `null` on a nullable field CLEARS it, absent means no change. */
export interface CalendarBlockPatch {
  title?: string
  source?: CalendarWritableSource
  startsAt?: ISOTimestamp
  endsAt?: ISOTimestamp
  location?: string | null
  taskId?: string | null
  /** `{ state: 'accepted' }` is what Keep does. Dismiss is a delete, not a state. */
  state?: CalendarBlockState
  /**
   * REPLACED, NEVER MERGED — `[]` is how the picker removes them all, and omitting the key leaves
   * whatever the block had alone. That asymmetry is the Worker's own contract (PR 102), and it
   * is what lets a drag-to-move PATCH carry no `reminders` key and lose nothing.
   */
  reminders?: number[]
}

export interface AssignmentFilter {
  courseId?: string
  /** Inclusive upper bound on the due date. */
  dueBefore?: ISODate
  status?: Assignment['status']
  /**
   * Only assignments that still need action — 'upcoming' or 'missing'. This is what the Today
   * screen's "Due soon" card asks for: the next few deadlines INCLUDING anything overdue,
   * excluding what is already submitted or graded.
   */
  openOnly?: boolean
  limit?: number
}

export interface TaskFilter {
  done?: boolean
  list?: string
  dueBefore?: ISODate
}

export interface SaveFilter {
  topic?: string
  unread?: boolean
  limit?: number
}

// ── journal ──────────────────────────────────────────────────────────────────
// The two shapes the Journal screens (docs/design/handoff/"LifeOS Journal.dc.html", options
// 5a and 5b) need on top of the per-day read that already exists.
//
// Both are DERIVED — README §State Management lists counts, streaks and pace wording under
// "Derived, never stored". They are computed on the server side of the contract rather than in a
// component so that an LLM asking the Worker "how much have I written" gets the same numbers the
// screen shows, which is the machine-readability rule (CLAUDE.md → Architecture).

/**
 * A voice memo as the DAY PAGE needs it: the base record plus the two things the reading surface
 * cannot render without.
 *
 * These live here rather than on `VoiceMemo` in @app/shared because @app/shared is the vocabulary
 * the Worker and the browser SHARE, and the Worker has no voice-memo endpoint until V1 (the
 * transcription system). When that lands and the wire shape is real, both fields move down into
 * the shared type and this alias collapses to `VoiceMemo`.
 */
export interface JournalVoiceMemo extends VoiceMemo {
  /**
   * What the memo says, once something has said it. Null whenever `transcriptId` is null — the two
   * move together, and neither is ever an empty string standing in for the other. A queued memo has
   * NO transcript, which is a different thing from a transcript with nothing in it.
   */
  transcriptText: string | null
  /**
   * Which paragraph of the entry text the memo was dropped after, 0-based — the day page renders
   * memos INLINE AT THE POSITION THEY WERE DROPPED (docs/design/handoff/README.md §2, `5b`).
   *
   * Null means the position was never recorded, not "paragraph 0". Such a memo renders after the
   * text instead of at a guessed spot: a memo placed mid-entry by inference is the app claiming to
   * know where a thought interrupted a sentence, which it does not.
   */
  paragraphIndex: number | null
  /**
   * Why the desktop worker gave up, in its own plain words. Null unless `status` is `failed`.
   *
   * A failed memo that says nothing about why is indistinguishable from one that is still
   * waiting, and the owner's next move differs completely between the two.
   */
  error: string | null
}

/**
 * One recording, on its way from the browser to R2.
 *
 * The blob comes straight off MediaRecorder and is never re-encoded here: whatever the browser
 * produced is what the desktop worker decodes, so a transcription problem is traceable to one
 * file rather than to a conversion nobody watched.
 */
export interface VoiceMemoUpload {
  /** The journal day the memo lands on — the owner's local day, not UTC's. */
  date: ISODate
  blob: Blob
  /** What the recorder MEASURED. Never derived from the byte count. */
  durationSec: number
}

// ── phone lecture upload (2026-08-26) ───────────────────────────────────────
//
// The owner records lectures on his phone and, until this landed, Quick-Shared the file to a
// folder on the PC. These three shapes are what the /record screen needs to end that.
//
// WHY A SECOND LECTURE TYPE. `Lecture` in @app/shared is the SCHOOL screens' vocabulary:
// `courseCode`, `index` and `title` are all non-optional there because a lecture the School
// screens draw is always a lecture inside a course. The Worker's own lecture (its `LectureSchema`
// in apps/worker/src/lib/audio.ts) has all three NULLABLE, and that nullability is the entire
// point of this lane — a recording that arrives with no course must be visibly unfiled, not
// filed under `''`. Widening the shared type would ripple through every School component for a
// screen none of them draw, so this alias carries the Worker's real shape instead, exactly as
// `JournalVoiceMemo` does for memos. When School learns to render an unfiled lecture, the two
// converge.
export interface CapturedLecture {
  id: string
  /** Null means UNFILED — nothing guessed it and nothing should. */
  courseCode: string | null
  /**
   * Null when nothing named it. A phone upload sets it to the picked file's own name, verbatim
   * ('Voice 013.m4a') — that is provenance the owner can recognise, not a title invented for him.
   */
  title: string | null
  /** 1-based number within a course. Null for anything the owner did not number. */
  index: number | null
  /** The day the class happened, not the day the file was uploaded. */
  date: ISODate
  durationSec: number | null
  status: Lecture['status']
  audioLocation: Lecture['audioLocation']
  /** Whether the desktop worker has actually returned words. Never inferred from `status`. */
  hasTranscript: boolean
}

/** One lecture recording on its way from the phone (or a laptop's file picker) to R2. */
export interface LectureUpload {
  /** The file the owner picked. Uploaded byte-for-byte; nothing re-encodes it. */
  file: File
  /** The course to file it under, or null for "assign later". A choice, never a guess. */
  courseCode: string | null
  /** The day the class happened. Defaults to today at the call site, not here. */
  date: ISODate
  /**
   * How long the recording runs, if the browser could read it off the file cheaply.
   *
   * Null when it could not — a duration guessed from the byte count would be wrong by whatever
   * bitrate the phone happened to use, and the lecture page would state it as fact.
   */
  durationSec?: number | null
  /**
   * Fires as the bytes actually leave — `sent` of `total`, both in bytes.
   *
   * IT IS REAL PROGRESS OR IT IS NOTHING. A 100 MB lecture over phone data takes minutes, and a
   * bar animated on a timer would keep moving after the connection died. The remote
   * implementation reads XMLHttpRequest's own upload events; demo mode never calls this at all.
   */
  onProgress?: (sent: number, total: number) => void
  /** Aborts the upload in flight. The job row stays behind, `awaiting_audio`, and can be retried. */
  signal?: AbortSignal
  /**
   * Reuse an already-registered job instead of registering a second one.
   *
   * This is what makes RETRY safe: the two-call contract exists so a dropped PUT can be repeated
   * against the same job (apps/worker/src/routes/audio.ts), and re-registering would leave a
   * trail of `awaiting_audio` rows for one recording.
   */
  resume?: RegisteredUpload
  /**
   * Called the instant the job exists, BEFORE a single byte goes out.
   *
   * The caller keeps what it hands back so that a failure halfway through 80 MB can be retried
   * against the same job — which is the difference between "try again" and "start again".
   */
  onRegistered?: (job: RegisteredUpload) => void
}

/** The half of the two-call intake contract that survives a failed upload. */
export interface RegisteredUpload {
  jobId: string
  uploadUrl: string
  /** The lecture row the register call created. Null only when this upload attached to no lecture. */
  lectureId: string | null
}

// ── the durable phone queue (REC-1b) · the three calls it drives ─────────────
//
// `uploadLecture` above is ONE ATTEMPT, watched: a person is looking at a progress bar and will
// see it fail. The queue is the other half — a recording that survives the app being killed,
// the phone going into a tunnel, and the owner never looking at this screen again. It needs the
// two-call intake contract taken apart into its three real requests, because it has to be able
// to re-register, re-send, and ASK, independently and in that order
// (apps/worker/src/routes/audio.ts; PR #98 is where the fences it relies on were built).

/**
 * The server's own words for where an upload stands. Never a local invention.
 *
 * The queue mirrors these rather than deriving a status of its own, because a client-side guess
 * about whether audio arrived is exactly the guess that ends with a deleted recording.
 */
export type UploadServerStatus =
  | 'awaiting_audio'
  | 'uploading'
  | 'queued'
  | 'claimed'
  | 'transcribing'
  | 'done'
  | 'failed'
  | 'cancelled'

/**
 * The statuses that are EVIDENCE the audio arrived, and no others.
 *
 * `uploading` is not one of them: an attempt holds the job and may still fail. `awaiting_audio`
 * is not one either. This list is the server's own `RECEIPT_CONFIRMED_STATUSES`
 * (apps/worker/src/lib/audio.ts) and the two must not drift.
 */
export const RECEIPT_CONFIRMED_STATUSES: readonly UploadServerStatus[] = [
  'queued',
  'claimed',
  'transcribing',
  'done',
]

/** `POST /api/capture-tokens`'s body. `device` is for the token list, and nothing more. */
export interface CaptureTokenInput {
  label: string
  device?: CaptureToken['device']
}

/**
 * A freshly minted capture token: the stored row's metadata plus the plaintext, which the Worker
 * returns in this one response and never again (apps/worker/src/routes/capture-tokens.ts).
 */
export interface CreatedCaptureToken {
  id: string
  label: string
  device: CaptureToken['device']
  createdAt: string
  token: string
}

/** One registration attempt, named by the client so a retry converges instead of duplicating. */
export interface UploadRegistrationInput {
  /** Minted once per recording, on the phone, BEFORE the first request. Reused by every retry. */
  clientUploadId: string
  contentType: string
  filename: string
  /** The literal filename. The owner recognises 'Voice 013.m4a'; a prettied name is invention. */
  title: string
  courseCode: string | null
  /** The day the class happened. Resolved by the caller, so a retry cannot mean a different day. */
  date: ISODate
  durationSec: number | null
}

/** What `POST /api/audio/uploads` answered. */
export interface UploadRegistration extends RegisteredUpload {
  /**
   * 201 created it, 200 found the job an earlier call already made.
   *
   * Carried because it is the difference between "this phone registered once" and "the response
   * to the first attempt was lost" — worth logging, never worth a different code path.
   */
  created: boolean
  maxBytes: number
}

/** One `PUT {uploadUrl}` of the bytes. */
export interface UploadBlobInput {
  uploadUrl: string
  blob: Blob
  contentType: string
  /** Bytes handed to the socket. Real progress or none — never a timer. */
  onProgress?: (sent: number, total: number) => void
  signal?: AbortSignal
}

/** What a `PUT` that was ACCEPTED answered. A refusal throws `UploadApiError` instead. */
export interface UploadBlobResult {
  /** The server's record of receipt — not a claim about what is in the bucket right now. */
  received: boolean
  /** False when the job already had its audio and these bytes were deliberately not stored. */
  written: boolean
  /** The job's status after the write. Mirrored, never assumed. */
  status: UploadServerStatus
  message: string | null
}

/**
 * What `GET /api/audio/uploads/{jobId}` answered — the only thing the queue treats as a receipt.
 *
 * `clientUploadId` is on here for one reason: a job id alone proves nothing about WHOSE recording
 * that job is holding. The queue compares it against the entry's own before it is willing to
 * delete a local blob (lib/upload-queue.ts → `receiptFrom`).
 */
export interface UploadState {
  jobId: string
  status: UploadServerStatus
  clientUploadId: string | null
  bytes: number | null
  lastError: string | null
  lectureId: string | null
}

/**
 * A refusal the Worker WROTE, carried with its status and its machine-readable code.
 *
 * The rest of this file's calls collapse a failure into a sentence, which is right for a screen
 * with a person in front of it. The queue is a state machine: `409 upload_in_progress` and
 * `409 upload_superseded` mean opposite things about whether to try again, and a driver that
 * had to match on prose would be one copy edit away from re-sending a lecture forever.
 */
export class UploadApiError extends Error {
  readonly status: number
  readonly code: string
  /** The job's status off the error body, when the Worker sent one. */
  readonly serverStatus: UploadServerStatus | null
  /** Seconds, off `Retry-After`. Null when the Worker named none. */
  readonly retryAfterSec: number | null

  constructor(
    status: number,
    code: string,
    message: string,
    options: { serverStatus?: UploadServerStatus | null; retryAfterSec?: number | null } = {},
  ) {
    super(message)
    this.name = 'UploadApiError'
    this.status = status
    this.code = code
    this.serverStatus = options.serverStatus ?? null
    this.retryAfterSec = options.retryAfterSec ?? null
  }
}

/** One cell of the writing heatmap. A day with no entry is present with `words: 0`, never absent. */
export interface JournalHeatDay {
  date: ISODate
  words: number
}

/** Per-month coverage, for the month picker and its honest "14 of 17 days written" line. */
export interface JournalMonth {
  /** 'YYYY-MM' — the key the index filters by. */
  month: string
  /** 'Aug' — the picker's button label. */
  label: string
  /** Days in the month that carry an entry. */
  written: number
  /** Days of the month that have actually happened (the current month stops at today). */
  days: number
  /** The longest run of consecutive written-less days inside the month. */
  longestGap: number
}

export interface JournalStats {
  entryCount: number
  wordCount: number
  /** Rounded. Zero when there are no entries — never a division by zero rendered as NaN. */
  averageWords: number
  /** Consecutive days written ending today. Zero when today and yesterday are both blank. */
  currentStreak: number
  longestStreak: number
  /** Entries in the last seven days, today included. */
  entriesThisWeek: number
  /** Entries carrying at least one voice memo — the "Has voice" filter chip's count. */
  withVoice: number
  /** 26 weeks ending today, oldest first, one row per day including the blank ones. */
  heatmap: JournalHeatDay[]
  /** Tag → entries carrying it, most used first. */
  tags: Array<{ tag: string; count: number }>
  /** Newest month first. */
  months: JournalMonth[]
  /**
   * The weekday the owner writes most on and the one he writes least on, by average word count.
   * Null until there is enough spread to say anything — an observation with no evidence behind it
   * is exactly the invented insight the product forbids (CLAUDE.md → Product identity rules).
   */
  quietPattern: { longestWeekday: string; shortestWeekday: string } | null
}

/**
 * THE READ-ONLY "THAT DAY" STRIP (`5b`). What the rest of the system knows about one date, in the
 * four sections the mockup draws. Deliberately non-interactive: editing history from a reading
 * surface is a trap (README §2).
 *
 * Every list can be empty, and an empty list here means "nothing that day", which is a true
 * statement about the demo dataset. It is NOT what a failed read looks like — that throws.
 */
export interface JournalDayContext {
  date: ISODate
  /** Classes and sessions that met that day. */
  attended: Array<{ title: string; detail: string | null }>
  /** Tasks completed that day, by title. */
  completedTasks: string[]
  /** Learning items finished that day, with the minutes actually logged. */
  learning: Array<{ title: string; minutes: number | null }>
  /** Which day of the learning streak this was, or null when the streak did not reach it. */
  streakDay: number | null
  /** Things saved to Interests that day. */
  saves: Array<{ title: string; detail: string | null }>
}

// ── interests (LifeOS Interests.dc.html, option 7a) ──────────────────────────
//
// Two view-shaped reads on top of the saves corpus, here rather than in @app/shared for the
// same reason JournalStats is: they are DERIVED answers the contract computes, not rows the
// Worker stores. Both are what an LLM would ask for too — "how big is my library", "what is
// near this save" — which is the machine-readability rule doing its job.

/**
 * THE HEADER BAND'S THREE FACTS. Every one is a count over the corpus, and none of them is
 * allowed to be a constant: a hard-coded "247 items" is the app claiming to know the size of a
 * library it did not measure.
 */
export interface InterestsOverview {
  /** Everything saved, clustered or not. */
  totalSaves: number
  /**
   * The inbox: saved, unread, and not yet in a cluster. Three things being true at once, which
   * is what an inbox is — arrived and not dealt with. A save that is merely unread is not in it.
   */
  inboxCount: number
  /**
   * The backlog import that filled the library, or null when no import has run. Kept as facts
   * (`items`, `done`) rather than the sentence the band renders, so the band can say "312 items"
   * while it is running and the same shape can say what is left.
   */
  backlogImport: { source: string; items: number; done: boolean } | null
}

/**
 * ONE SAVE, OPENED — the right-hand detail pane.
 *
 * `related` is the nearest-neighbour result the pane labels "by meaning, not tags". It is a
 * list of saves rather than a list of ids because the pane renders titles, sources and dates,
 * and a second round trip per row would be three requests to draw three lines. Empty is a real
 * answer: nothing in the library is close enough, and the pane says that instead of padding
 * itself with whatever was saved nearby in time.
 */
export interface InterestSaveDetail {
  save: InterestSave
  related: InterestSave[]
}

/** ── SCHOOL ── announcements. Unread is a real state; `read` is never inferred from age. */
export interface AnnouncementFilter {
  courseId?: string
  unreadOnly?: boolean
  limit?: number
}

/**
 * The whole study set, as ONE generation produces it.
 *
 * The three reads (`getLectureSummary`, `getFlashcards`, `getPracticeQuestions`) split one
 * endpoint's answer three ways because three panels ask separately. The WRITE cannot be split:
 * one model call makes all three from one transcript and they share one provenance, so what it
 * returns is the set. `note` is the degradation sentence — a tier that could not be served says
 * so rather than quietly serving another model under its name.
 */
export interface StudySet {
  summary: LectureSummary | null
  flashcards: Flashcard[]
  practiceQuestions: PracticeQuestion[]
  note: string | null
}

/** A lecture plus its transcript and the segments that carry the citation timestamps. */
export interface LectureTranscript {
  lecture: Lecture
  transcript: Transcript
  /** Ordered by `index`, never overlapping. May be an excerpt of a longer transcript. */
  segments: TranscriptSegment[]
}

// ── LEARNING ── the two shapes the Learning screens need ─────────────────────
// (docs/design/handoff/"LifeOS Learning.dc.html", options 9a overview and 9b track page.)
//
// These live here rather than on @app/shared for the same reason JournalStats does: @app/shared is
// the vocabulary the Worker and the browser SHARE, and the Worker has no learning endpoint until
// V3. When the track-ingestion API lands and the wire shape is real, they move down.
//
// EVERYTHING DERIVED IS DERIVED HERE, on the server side of the contract, never in a component.
// README §State Management puts "pace-ahead/behind wording" under "Derived, never stored", and the
// machine-readability rule (CLAUDE.md → Architecture) says an LLM asking the Worker "am I behind on
// algebra" must get the same sentence the screen shows. A component that computed its own pace
// would be a second answer to the same question.

export type LearningPaceDirection = 'ahead' | 'behind' | 'on_plan'

/**
 * How far off the plan line the track is, in the plan's own unit: one item per day, so the number
 * of items and the number of days are the same number. Nothing here is stored — it is
 * `itemsDone − itemsPlannedByToday`, recomputed on every read.
 */
export interface LearningPace {
  /** Positive is ahead, negative is behind, zero is exactly on the line. */
  days: number
  direction: LearningPaceDirection
  /** The plain words the screen prints: '2 days ahead', '4 days behind', 'on the plan line'. */
  phrase: string
}

export interface LearningUnitItem {
  id: string
  /** '3.4 Solving two-step equations · 12 problems' — the plan's own wording. */
  title: string
  /** Position in the whole track, 0-based. The plan's order is the item's identity. */
  index: number
  /** The day the plan assigns it to. */
  plannedDate: ISODate
  done: boolean
  /**
   * The day it was actually finished — which is NOT the planned day whenever the track is ahead or
   * behind. Null while it is open, and never quietly filled in from the plan.
   */
  completedDate: ISODate | null
  /** Logged minutes, or null. An unfinished item has no minutes and none are invented. */
  minutes: number | null
  /**
   * THE ONE THING TO OPEN for this item (docs/LEARNING-PLAN-FORMAT.md v2). Null when the plan
   * named none — and the row then shows NO button rather than a dead one falling back to the
   * track's library. Hidden is not the same as missing.
   */
  resource: LearningResource | null
  /**
   * The PLAN's estimate for this sitting, 5–240. Never mixed with `minutes`: one is a forecast the
   * plan made, the other is a fact about a day that happened. Null when the plan declined to guess.
   */
  minutesEstimate: number | null
  /** The plan's own how-to, verbatim. '' when it said nothing, and the block is then absent. */
  instructions: string
}

/**
 * ONE ROW OF THE RESOURCES SECTION — a link, plus what still points at it.
 *
 * Deduplicated by url in the contract, not here: a plan naming "PCC ch. 1-2" and "PCC ch. 3" is
 * naming one book twice, and two rows for one link would make the library a worse index than the
 * plan it came from.
 */
export interface LearningLibraryEntry extends LearningResource {
  /** `track` — the plan's standing library; `item` — collected off the items that name it. */
  origin: 'track' | 'item'
  /** How many items point at this url. 0 for a standing resource nothing references. */
  itemCount: number
  itemsDone: number
  /** The next unfinished item that uses it, in plan order. Null when there is none. */
  nextItemTitle: string | null
}

/** One track's shelf. `entries` empty means the plan named no links at all — a real answer. */
export interface LearningTrackLibrary {
  trackId: string
  trackName: string
  entries: LearningLibraryEntry[]
}

/** What a session carries beyond the check itself. Both optional and both meaning it. */
export interface LearningSessionInput {
  /** How long it ACTUALLY took. Omitted means untimed — never zero, which is a measurement. */
  minutes?: number | null
  /** The owner's own words. Omitted means he wrote none. */
  note?: string | null
}

export type LearningUnitState = 'done' | 'current' | 'future'

export interface LearningUnit {
  id: string
  /** 'Unit 3 · Linear equations' */
  name: string
  /** 1-based. */
  number: number
  state: LearningUnitState
  itemsTotal: number
  itemsDone: number
  /** The day the last item in the unit was finished. Null unless the whole unit is done. */
  completedDate: ISODate | null
  /** The plan's first date for this unit. */
  startsOn: ISODate
  /** Which week of the plan the current unit is being worked in. Null on any other unit. */
  week: number | null
  items: LearningUnitItem[]
}

/**
 * One track as the OVERVIEW (`9a`) needs it. The card states a pace, offers today's item, and
 * counts the week — all three derived from the same session data, so they cannot disagree.
 */
export interface LearningTrackSummary {
  track: LearningTrack
  /** Rounded. `25` renders as '25% · 46 of 182'. */
  percent: number
  pace: LearningPace
  /** 'Unit 3 · Linear equations' — the unit the next unfinished item lives in. */
  currentUnit: string
  /**
   * The next unfinished item in plan order: what the card offers to check off today. Null once the
   * plan is finished. It is deliberately NOT "the item the plan dates today" — a track that is two
   * days ahead has already done that one, and offering it again would be the app forgetting.
   */
  nextItem: LearningUnitItem | null
  /** The one after it, for the card's NEXT line. Null at the end of the plan. */
  followingItem: LearningUnitItem | null
  /**
   * A time estimate for `nextItem`, rounded to five minutes — the track's OWN average minutes per
   * finished item, not a population norm (CLAUDE.md → Product identity rules). Null until enough
   * items have been logged to average anything.
   */
  estimateMinutes: number | null
  /** Monday-first, seven entries, the calendar week `today` falls in. Future days carry 0. */
  week: Array<{ date: ISODate; items: number }>
  /** Items finished this week, and items the plan put in this week. */
  weekDone: number
  weekPlanned: number
  /** The last day anything was finished on this track. Null when nothing ever was. */
  lastActive: ISODate | null
  /**
   * Consecutive days ending TODAY that this track was worked on. Zero the moment a day is missed —
   * which is what makes the card fall back to "last: 3 days ago" instead of quietly showing a
   * streak that stopped. Per-track, not the combined streak on the heatmap: a card that borrowed
   * the other track's run would be claiming work that happened somewhere else.
   */
  streakDays: number
}

/** One day's work on one track, as the track page's session log lists it. */
export interface LearningSession {
  date: ISODate
  /** Titles finished that day, in plan order. Never empty — a session with no items is not one. */
  items: string[]
  minutes: number
  /** The owner's own words about that session. Null when he wrote none — never an empty string. */
  note: string | null
}

/** One day on the pace chart: what was actually done against what the plan expected. */
export interface LearningPacePoint {
  date: ISODate
  done: number
  planned: number
}

/** One track as the TRACK PAGE (`9b`) needs it: provenance, curriculum, pace, sessions. */
export interface LearningTrackDetail {
  track: LearningTrack
  percent: number
  pace: LearningPace
  /** Every unit, in plan order, each carrying every one of its items. */
  units: LearningUnit[]
  /** Oldest first, one point per day from the plan's first day to today. */
  paceSeries: LearningPacePoint[]
  /** Newest first. */
  sessions: LearningSession[]
  totalMinutes: number
  nextItem: LearningUnitItem | null
}

// ── CHAT (11a/11b) and the ⌘K CAPTURE PALETTE (11c) ──────────────────────────
// (docs/design/handoff/"LifeOS Chat.dc.html".)
//
// Its own import line rather than another name in the block at the top of the file: three screen
// ports are in flight at once and a one-identifier edit inside a shared import list is a merge
// conflict for no benefit.

import type { ISOTimestamp } from '@app/shared'
//
// These live here rather than in @app/shared for the same reason JournalStats and the Learning
// shapes do: @app/shared is the vocabulary the Worker and the browser SHARE, and the Worker has
// no chat endpoint until V4 and no capture endpoint until V5. When those land and the wire shapes
// are real, they move down.
//
// THE ONE INVARIANT THIS DOMAIN EXISTS TO CARRY (README §Overview, §State Management, and
// CLAUDE.md → Product identity): **every claim about the user's life carries at least one source
// ref.** It is enforced structurally — `ChatMessage.sources` is not optional, an assistant message
// with an empty `sources` array is a bug the unit test catches, and every `locator` string a
// source card renders is DERIVED FROM THE ENTITY IT CITES rather than typed. A card that says
// "COGSCI 175 · Lecture 5 · 13:04" says it because a lecture with index 5 and a transcript segment
// starting at 784,600ms both exist; if either stopped existing the string would change, which is
// the whole point.

/**
 * Which corner of the corpus a thread is allowed to read. Per-thread state that persists with the
 * thread (README §Interactions → Chat); changing it applies to subsequent messages only.
 *
 * `course:<courseId>` is the one parameterised scope — the mockup's `NEU C61` pill. A course scope
 * admits that course's material AND the journal entries that mention the course, which is what the
 * mockup's own thread does when it answers "have I written anything about being behind".
 */
export type ChatScopeId =
  | 'everything'
  | 'journal'
  | 'school'
  | 'interests'
  | 'learning'
  | `course:${string}`

/** One term of the scope-size line: `{ count: 4, noun: 'lecture' }` → "4 lectures". */
export interface ChatScopeSizePart {
  count: number
  /** Singular. The label helper pluralises; storing both forms is two places to disagree. */
  noun: string
}

export interface ChatScope {
  id: ChatScopeId
  /** 'Everything', 'Journal', 'COGSCI 175' — the pill's text. */
  label: string
  /**
   * How much this scope can actually see, DERIVED and never stored (README §State Management puts
   * "scope-size lines" under Derived, never stored). Parts with a count of zero are omitted, so a
   * scope over nothing renders an empty array rather than "0 lectures" — a scope that states a
   * zero is a scope that sounds broken instead of empty.
   */
  size: ChatScopeSizePart[]
  /** Which hue the composer's scope-chip dot takes. A course scope reads as School. */
  domain: 'everything' | 'journal' | 'school' | 'interests' | 'learning'
}

// V4 APPENDED THREE KINDS: 'assignment', 'task', 'learning_item'. The handoff drew five, all of
// them School or Journal artifacts — it never drew a Learning citation, because it never drew a
// Learning answer. But `ChatScopeId` HAS a `learning` scope, and a scope whose every possible
// source is uncitable is a scope that can only ever produce the red "unverified" strip. Filling
// the gap is not overriding the handoff; leaving it would ship a scope that cannot work.
// 2026-08-28 APPENDED A NINTH: 'course_file'. It is NOT a duplicate of 'syllabus' above it —
// `syllabus` cites what a MODEL read out of a course's syllabus, `course_file` cites the PDF a
// text extractor transcribed out of bCourses. One carries provenance and one is the professor's
// own words; a single kind for both would let an answer credit the wrong author.
export type ChatSourceKind =
  | 'transcript'
  | 'announcement'
  | 'syllabus'
  | 'journal'
  | 'voice_memo'
  | 'assignment'
  | 'task'
  | 'learning_item'
  | 'course_file'

/**
 * ONE SOURCE CARD — the thing that makes an answer checkable.
 *
 * Every field is resolved against a real entity in the corpus by the data layer. Nothing here is
 * a sentence a model wrote about a sentence a model wrote.
 */
export interface ChatSource {
  kind: ChatSourceKind
  /** 'TRANSCRIPT', 'ANNOUNCEMENT', 'SYLLABUS · PDF', 'JOURNAL', 'VOICE MEMO' — the type pill. */
  pill: string
  /** 'COGSCI 175 · Lecture 5 · 13:04'. DERIVED from the cited entity, never typed. */
  locator: string
  /** The quiet third line: 'Sep 16 · 2 matches', 'extracted Aug 26', or null when there is none. */
  detail: string | null
  /**
   * The route this card opens — always a real route in this app, never a dead card. A transcript
   * source carries the matched phrase in `?q=`, which is how the lecture page lands the reader on
   * the segment rather than at the top of a 87-minute recording.
   */
  href: string
  /**
   * Where in the recording, for a transcript source. Non-null is exactly what makes the card show
   * the green `Jump to timestamp` affordance instead of a date line.
   */
  timestampMs: number | null
}

/** The amber scope-limit strip: what the scope stopped the answer reading, and the one way out. */
export interface ChatScopeLimit {
  /** 'Scope was COGSCI 175, so this answer did not read your other three courses.' */
  text: string
  /** The one action. Re-scoping the thread is real; re-running the question is V4. */
  widenTo: ChatScopeId
  widenLabel: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  /**
   * At least one for every assistant message. NOT optional and never empty: an uncited claim about
   * the owner's life is a design bug, and the design shows it caught rather than shipped (README
   * §Interactions → Chat). A user message carries none, because a question is not a claim.
   */
  sources: ChatSource[]
  scopeLimit: ChatScopeLimit | null
  /**
   * The pictures sent WITH this question. Always empty on an assistant message: the model reads
   * images, it does not send them. See the CHAT v3 block at the end of this file.
   */
  attachments: ChatAttachment[]
  /**
   * What this answer SUGGESTED adding to the calendar or the reminders. Nothing has been created
   * — each is a card with Accept and Reject. Always empty on a user message.
   */
  proposals: ChatProposal[]
}

export interface ChatThread {
  id: string
  title: string
  scopeId: ChatScopeId
  /** The scope pill's text on the list row — resolved here so the row needs no second read. */
  scopeLabel: string
  /** How long the conversation is. The list row's '4 messages'. */
  messageCount: number
  /** The header's 'started 08:04'. */
  startedAt: ISOTimestamp
  /** What TODAY / THIS WEEK / EARLIER is derived from. */
  updatedAt: ISOTimestamp
}

/**
 * ONE STARTER CARD on the empty thread (11b).
 *
 * `reads` is the card's whole point: each starter names what it would actually open before you
 * press it, and the number is DERIVED from the corpus rather than written on the card. A starter
 * that says "reads 9 transcripts" over a corpus holding four is the app over-promising in the one
 * place it is trying to establish that it does not.
 */
export interface ChatStarter {
  id: string
  /** The question, in the owner's own phrasing. */
  prompt: string
  /** The scope it would run at, so pressing it sets the thread's scope as well as its text. */
  scopeId: ChatScopeId
  reads: ChatScopeSizePart[]
  /** Which glyph the card wears — the domain the reading comes out of. */
  domain: 'school' | 'interests' | 'journal'
}

export interface ChatThreadDetail {
  thread: ChatThread
  /**
   * The conversation. MAY BE SHORTER THAN `thread.messageCount`, and the screen says so in words
   * when it is — the demo dataset ships one fully written conversation and cannot invent the rest,
   * and inventing them would mean inventing cited answers, which is the one thing this screen
   * exists to not do.
   */
  messages: ChatMessage[]
}

// ── capture (11c) ────────────────────────────────────────────────────────────

export type CaptureType = 'task' | 'note' | 'clip' | 'reminder'

export interface CaptureInput {
  text: string
  /** Null means the type was left unset, which files to the Inbox. Nothing here is required. */
  type: CaptureType | null
  /** A date the palette parsed out of the text, when it found one. Never guessed. */
  due?: ISODate | null
  /**
   * For `type: 'reminder'` only: the instant to nudge at, when the owner picked one (the phone's
   * preset chips, 2026-09-14). Omitted, the adapter keeps its long-standing default — 9:00 on
   * `due` (or today) in the owner's own zone. Ignored for every other type.
   */
  reminderAt?: string | null
}

/** What the capture actually became, in the words the palette reports back. */
export interface CaptureResult {
  /** The id of the row created, in its own domain. */
  id: string
  type: CaptureType | null
  /** 'Tasks · Inbox', 'Interests · Inbox' — where it LANDED, not where it was meant to go. */
  destination: string
  /** The route that now contains it, so `⌘⏎ save and open` has somewhere real to go. */
  href: string
}

/**
 * What a pasted link resolves to. `title` is null until something has actually fetched it — the
 * palette never derives a title from a URL, because a title invented from a slug is exactly the
 * fabricated knowledge this product refuses. `sourceDetail` IS derived from the URL, because
 * "r/comfyui" is written in the URL rather than fetched from the page.
 */
export interface ClipPreview {
  url: string
  title: string | null
  /** 'r/comfyui', 'youtube.com'. */
  sourceDetail: string
  /** 'Interests' — the domain a clip files under, stated before it is saved. */
  filesUnder: string
}

export interface DataAPI {
  /** True when the app is running on the in-memory dataset. Drives the "Demo data" badge. */
  readonly isDemo: boolean

  // meta
  getClock(): Promise<Clock>
  getSyncStatus(): Promise<SyncStatusRow[]>

  // journal
  getJournalEntry(date: ISODate): Promise<JournalEntry | null>
  getJournalEntries(range: DateRange): Promise<JournalEntry[]>
  saveJournalEntry(input: JournalEntryInput): Promise<JournalEntry>
  /** The memos dropped into one day's entry, oldest first. Empty is a real answer. */
  getVoiceMemos(date: ISODate): Promise<JournalVoiceMemo[]>
  /**
   * Send one recording to the transcription pipeline and return the memo it became.
   *
   * The returned memo is `queued`, never `transcribed`: this call ends when the bytes are safely
   * in R2, and the desktop worker has not seen them yet. Anything that claimed otherwise would be
   * inventing a transcript.
   */
  uploadVoiceMemo(input: VoiceMemoUpload): Promise<JournalVoiceMemo>
  /** Totals, streaks, the heatmap window and the tag counts behind the Journal index (`5a`). */
  getJournalStats(): Promise<JournalStats>
  /** What the rest of the system knows about one date — the read-only strip on `5b`. */
  getJournalDayContext(date: ISODate): Promise<JournalDayContext>

  // school
  getTerm(): Promise<Term | null>
  getCourses(): Promise<Course[]>
  getClassOccurrences(date: ISODate): Promise<ClassOccurrence[]>
  getAssignments(filter?: AssignmentFilter): Promise<Assignment[]>
  getLectures(courseId?: string): Promise<Lecture[]>
  getLectureTranscript(lectureId: string): Promise<LectureTranscript | null>

  // ── phone lecture upload (`/record`) ──────────────────────────────────────
  //
  // Separate from `getLectures` above, and deliberately so: those three speak the School screens'
  // vocabulary, where a lecture always has a course. These speak the recording's own — where it
  // very often does not yet.

  /**
   * Send one lecture recording to the transcription pipeline and return the lecture it became.
   *
   * The returned lecture is `audio_uploaded` with `hasTranscript: false`, always: this call ends
   * when the bytes are in R2 and the desktop PC has not looked at them. Anything else would be
   * inventing a transcript.
   */
  uploadLecture(input: LectureUpload): Promise<CapturedLecture>
  /** Newest first. What `/record` lists underneath the form so an unfiled recording is findable. */
  getCapturedLectures(limit?: number): Promise<CapturedLecture[]>
  /** One recording by id. Null means no such lecture — a real answer, not a failure. */
  getCapturedLecture(id: string): Promise<CapturedLecture | null>
  /** File a recording under a course, or pass null to unfile it. Writes for real. */
  assignLectureCourse(id: string, courseCode: string | null): Promise<CapturedLecture>

  // ── the durable queue's three calls (REC-1b) ──────────────────────────────
  //
  // Deliberately NOT folded into `uploadLecture`: that method is one watched attempt from start
  // to finish, and the queue needs to be able to stop between any two of these three and resume
  // in a different session, on a different network, after the app was killed.

  /**
   * Register (or re-find) one named upload. Idempotent on `clientUploadId`.
   *
   * A LOST RESPONSE IS NEVER A REASON TO REGISTER AGAIN WITH A NEW NAME. Retrying with the same
   * `clientUploadId` converges on the job the first call made; minting a new one is how a single
   * lecture becomes two rows and two lectures.
   */
  registerUpload(input: UploadRegistrationInput): Promise<UploadRegistration>
  /**
   * Send the bytes. Only ever called after a read-back said `awaiting_audio`.
   *
   * Every refusal arrives as `UploadApiError` with the Worker's own code, because the difference
   * between "another attempt holds it" and "this job is closed" decides whether the recording is
   * ever sent again.
   */
  putUploadBlob(input: UploadBlobInput): Promise<UploadBlobResult>
  /**
   * Ask what actually happened. The queue's ONE source of truth about the server.
   *
   * Nothing else is allowed to stand in for it — not a PUT's status code, not a 409's prose, not
   * the order the statuses happen to fall in.
   */
  getUploadState(jobId: string): Promise<UploadState>

  // ── school · the three School screens (6a/6b/6c) ──────────────────────────
  // Everything below either comes from Canvas (V2) or is built on a transcript (V1). The AI
  // ones return artifacts that carry { model, sourceRef, generatedAt }; see
  // packages/shared/src/school.ts for why that is structural rather than a convention.

  /** One course, addressed the way the URL addresses it (`courseSlug`, e.g. 'cogsci-175'). */
  getCourse(courseSlug: string): Promise<Course | null>
  /** Newest first. Read and unread both come back — the read ones are the "· read" rows. */
  getAnnouncements(filter?: AnnouncementFilter): Promise<Announcement[]>
  /**
   * Marks announcements read — what expanding one in the School feed does.
   *
   * `read` means "read in bCourses, OR opened here", and it is a LATCH: this sets it, nothing
   * clears it, and a Canvas sync ORs bCourses' own state on top instead of overwriting. Nothing
   * is written back to Canvas; the connector is read-only in that direction and stays that way.
   *
   * Answers the ids that ACTUALLY changed, which is never assumed to be the ids that were sent —
   * marking a read row read again is a no-op that honestly reports itself as one.
   */
  markAnnouncementsRead(ids: string[]): Promise<string[]>
  /** Null when no syllabus has been read for this course. Null is a real, sayable answer. */
  getSyllabus(courseId: string): Promise<SyllabusExtract | null>
  /**
   * THE SYLLABUS FILE, not what a model made of it.
   *
   * Canvas lists a course's files, and for most Berkeley courses the syllabus is one of them — a
   * PDF that `courses.syllabus_body` has never contained. This answers which file that is and the
   * text a PDF reader pulled out of it, with no model anywhere in the path and therefore no
   * provenance chip.
   *
   * Null when the catalog holds nothing whose name or folder says "syllabus" — a real state, and
   * the one where the typed-syllabus flow is all there is. A file that was FOUND and could not be
   * read comes back with `text: null` and `file.extractionError` in words, because "found, could
   * not be read" and "there is none" are different sentences and the card says both.
   */
  getSyllabusDocument(courseId: string): Promise<SyllabusDocument | null>
  /**
   * Read this course's Canvas syllabus now, and answer what came out of it.
   *
   * REJECTS RATHER THAN RETURNING AN EMPTY EXTRACT, and the rejection's sentence is the point:
   * "no syllabus posted in bCourses" (the common case — the instructor attached a PDF Canvas
   * does not expose) and "the syllabus states no grading weights" are different facts, and the
   * panel repeats whichever one it got instead of a house error.
   */
  generateSyllabus(courseId: string): Promise<SyllabusExtract>
  /** Null unless a model has actually summarised this lecture's transcript. */
  getLectureSummary(lectureId: string): Promise<LectureSummary | null>
  getFlashcards(lectureId: string): Promise<Flashcard[]>
  getPracticeQuestions(lectureId: string): Promise<PracticeQuestion[]>
  /**
   * Make the summary, the flashcards and the practice questions from this lecture's transcript.
   *
   * ONE CALL, because one model call makes all three and they share one provenance. Nothing runs
   * this on a schedule: a transcript landing does not produce study material, which is why the
   * lecture screen's "no summary yet" is a fact about what has been asked for, not a wait.
   */
  generateStudySet(lectureId: string): Promise<StudySet>

  // tasks
  getTasks(filter?: TaskFilter): Promise<Task[]>
  createTask(input: TaskInput): Promise<Task>
  toggleTask(id: string, done: boolean): Promise<Task>
  /** Set or clear a due date, move it to another list, rename it. Returns the saved task. */
  updateTask(id: string, patch: TaskPatch): Promise<Task>
  /** Gone, really gone — not completed. Returns the task that was removed. */
  deleteTask(id: string): Promise<Task>
  getReminders(): Promise<Task[]>

  // ── calendar (11a/11b) ────────────────────────────────────────────────────
  //
  // ONE READ, THREE WRITES, AND THE ASYMMETRY IS THE FEATURE. The read returns both what LifeOS
  // owns and what Canvas is owed; the writes can only ever touch the first, because
  // `CalendarBlockInput` has no way to say `canvas`. Brief rule 1 is therefore a type error
  // rather than a code review note.

  /** One range of the calendar, inclusive: LifeOS blocks plus derived, read-only Canvas due dates. */
  getCalendarRange(range: DateRange): Promise<CalendarRange>
  /** Create a block LifeOS owns — what click-to-compose and drag-to-schedule both do. */
  createCalendarBlock(input: CalendarBlockInput): Promise<CalendarBlock>
  /** Move, resize, rename, or accept. Rejects any id that is not a LifeOS block. */
  updateCalendarBlock(id: string, patch: CalendarBlockPatch): Promise<CalendarBlock>
  /** Really deletes it. There is no id here that addresses a Canvas event. */
  deleteCalendarBlock(id: string): Promise<CalendarBlock>

  // learning
  getLearningTracks(): Promise<LearningTrack[]>
  getLearningItems(date: ISODate): Promise<LearningItem[]>
  /**
   * Check an item off, or un-check it. `session` rides along on the way IN only — the Worker's
   * session write is idempotent on (owner, item), so calling again with minutes adds a duration to
   * something already ticked. Ignored entirely when `done` is false: unchecking is a delete, and
   * there is nothing to attach a note to.
   */
  toggleLearningItem(
    id: string,
    done: boolean,
    session?: LearningSessionInput,
  ): Promise<LearningItem>
  getLearningStreak(): Promise<LearningStreak>

  // ── interests (7a) ────────────────────────────────────────────────────────
  // The page's order is its argument: ask bar, clusters, library. These reads are in that
  // order too — the overview is what the band above the ask bar counts, the topics ARE the
  // clusters, and the saves plus one opened save are the library.
  // ── learning · the two Learning screens (9a/9b) ───────────────────────────
  // Two reads, not one per panel: every number on the overview has to agree with every number on
  // the track page, so they come out of one derivation each rather than being assembled per card.

  /** Both track cards on the overview (`9a`), pace and today's item already derived. */
  getLearningTrackSummaries(): Promise<LearningTrackSummary[]>
  /** One track page (`9b`). Null when no such track — a real, sayable answer, not a failure. */
  getLearningTrackDetail(trackId: string): Promise<LearningTrackDetail | null>
  /**
   * THE RESOURCES SECTION — every link the plans point at, per track, deduplicated by url. A
   * separate read rather than a walk over the summaries because the item-level links live on the
   * items, and pulling every track's whole outline into the browser to collect them would make a
   * library page cost a curriculum.
   */
  getLearningResources(): Promise<LearningTrackLibrary[]>
  /**
   * THE WHOLE DOMAIN AS ONE PAGE OF MARKDOWN — every track, every unit, every item in plan
   * order, the sessions' own minutes and notes, the streak, and how to propose a revision.
   *
   * It exists so the owner can talk about his learning with an AI OUTSIDE the app, on a
   * subscription he already pays for, rather than spending the runtime API budget on a coach
   * (CLAUDE.md → AI cost policy). Fetched on demand — pressing a button — never on page load: it
   * is the whole curriculum, and nothing on the screen reads it.
   *
   * The page is rendered by one pure formatter in @app/shared, so demo mode emits the same
   * document from demo data and the coach never reads a different shape depending on mode.
   */
  exportLearning(): Promise<string>

  // interests
  getInterestSaves(filter?: SaveFilter): Promise<InterestSave[]>
  /** The nightly clusters, most recently added to first. Counts and dates are derived. */
  getInterestTopics(): Promise<InterestTopic[]>
  /** The band's counts: library size, inbox size, and the backlog import behind them. */
  getInterestsOverview(): Promise<InterestsOverview>
  /** One save with its nearest neighbours. Null when no save has that id — not an empty save. */
  getInterestSave(id: string): Promise<InterestSaveDetail | null>

  // ── connections (settings · the trust surface) ────────────────────────────
  //
  // Four reads rather than one, because four different backends own them and
  // each has to be able to fail — or be honestly absent — on its own. A single
  // getConnectionsStatus() would have to name one version for all of them, and
  // the whole point of this screen is that it never speaks for a system it
  // cannot see.
  //
  // `scenario` is REVIEW-ONLY and only demo mode reads it (see
  // ConnectionsScenario). Passing it to `remote` changes nothing.

  getConnectors(scenario?: ConnectionsScenario): Promise<Connector[]>
  getDesktopWorker(scenario?: ConnectionsScenario): Promise<DesktopWorkerStatus>
  getCaptureTokens(scenario?: ConnectionsScenario): Promise<CaptureToken[]>
  /**
   * Mint a device capture token (POST /api/capture-tokens, owner session only). The plaintext
   * `token` is in THIS answer and in no other, ever — the Worker keeps only its hash. Its one
   * caller hands it straight to the Android app's uploader and never shows or stores it here.
   * Demo mode refuses in words: a fixture has no server a phone could upload to.
   */
  createCaptureToken(input: CaptureTokenInput): Promise<CreatedCaptureToken>
  getDataStatus(scenario?: ConnectionsScenario): Promise<DataStatus>
  /**
   * Pull one connector now (POST /api/connections/canvas/sync on the Worker — the same adapter
   * the six-hourly cron runs). Demo mode refuses in words: a fixture has no real cloud behind it
   * for a sync to reach, and pretending otherwise would be inventing a connection.
   */
  syncConnector(id: string): Promise<SyncResult>

  // ── search (10a/10b) ──────────────────────────────────────────────────────
  /**
   * One bar over every domain. Returns the literal block and the related block SEPARATELY —
   * the shape enforces the handoff's rule that they are never interleaved (README §Interactions
   * → Search). See SearchResults, at the end of this file, for what `related` is allowed to mean.
   */
  search(query: string): Promise<SearchResults>
  // ── chat (11a/11b) and capture (11c) ──────────────────────────────────────
  //
  // FIVE READS/WRITES, not one, because they fail on different schedules: threads and their
  // messages wait on the chat engine (V4), while the capture endpoint is V5 — and a screen that
  // named one version for both would send the owner off to wait for the wrong thing.
  //
  // The scopes read is separate again, and it is the one that stays honest longest: it counts
  // what is already in the corpus, so it works the day the corpus is real whether or not anything
  // can be asked of it yet.

  /** Every thread, newest activity first. The list column groups them by `updatedAt`. */
  getChatThreads(): Promise<ChatThread[]>
  /** One thread opened. Null when no thread has that id — a real answer, not a failure. */
  getChatThread(threadId: string): Promise<ChatThreadDetail | null>
  /**
   * Every scope the SCOPE row can offer, each already carrying its own derived size. One read
   * rather than one per pill: the sizes have to agree with each other and with the answer that
   * cites them, so they come out of one derivation.
   */
  getChatScopes(): Promise<ChatScope[]>
  /** The three starters on an empty thread, each already carrying what it would read. */
  getChatStarters(): Promise<ChatStarter[]>
  /**
   * ASK THE ONE CHAT ENGINE (V4). The single write on this screen.
   *
   * One method for both "start a thread" and "reply in one", because the Worker has one path for
   * both: a thread with a question in it and no answer under it is a conversation that half
   * happened, and two calls would make that state reachable.
   *
   * Returns the thread and the two new messages — the question and the answer with the sources it
   * was actually built from. It resolves even when the answer cites nothing: that case is rendered
   * behind the red "unverified" strip rather than thrown, because the design shows the bug caught.
   */
  sendChatMessage(input: ChatSendInput): Promise<ChatSendResult>
  /**
   * REMOVE A THREAD AND EVERY MESSAGE IN IT — the thread list's right-click menu, and the second
   * write on this screen.
   *
   * There is no archive and no trash on purpose: a conversation kept somewhere the app never shows
   * it is one the owner would have to be told about to know it still exists. Returns the thread
   * that was removed, so the caller can say what went rather than that "something" did.
   */
  deleteChatThread(threadId: string): Promise<ChatThread>
  /**
   * SEND A PICTURE UP (Chat v3). Returns the id to pass in `ChatSendInput.attachmentIds`.
   *
   * Uploading is its own call rather than part of the ask, because the picture is chosen before
   * the question is finished being typed — and because a paste that failed to upload should say
   * so while the composer is still open, not when the question is submitted.
   */
  uploadChatAttachment(file: Blob): Promise<ChatAttachment>
  /**
   * A URL an `<img>` can load for one already-sent picture.
   *
   * It is a METHOD and not a field on `ChatAttachment` because the bytes are behind the owner's
   * token: there is no public URL for an image sent into this chat and there will not be one, so
   * the data layer fetches it and hands back an object URL the browser can render.
   */
  getChatAttachmentUrl(attachmentId: string): Promise<string>
  /**
   * ACCEPT A PROPOSAL — the only path from a suggestion to a real row (Chat v3).
   *
   * Creates the calendar block or the reminder through the same API the Calendar and Reminders
   * screens write with, links it back to the proposal, and returns both. Accepting twice does not
   * create twice: the second call is refused and says what the first one made.
   */
  acceptChatProposal(proposalId: string): Promise<ChatProposalDecision>
  /** Say no. Nothing was ever created, so there is nothing to undo — that is the whole point. */
  rejectChatProposal(proposalId: string): Promise<ChatProposal>
  /** What a pasted link is, before it is saved. Null when the text is not a link at all. */
  getClipPreview(url: string): Promise<ClipPreview | null>
  /** The one capture endpoint, many mouths (CLAUDE.md → Architecture). Writes for real. */
  captureQuick(input: CaptureInput): Promise<CaptureResult>

  // ── notifications · Web Push (NOTIF-1a/1b) ────────────────────────────────
  //
  // THE SUBSCRIBE FLOW IS TWO CALLS AND A BROWSER PROMPT, in this order: read the VAPID public
  // key, hand it to `pushManager.subscribe()`, POST what that returns. lib/push.ts owns the
  // middle step — it is the only part of this that is not a fetch.

  /**
   * The VAPID public key, base64url, for `applicationServerKey`. NOT A SECRET: the point of it is
   * to be handed out. Throws the Worker's own sentence when this deployment has no keys, which is
   * what the Notifications screen prints instead of pretending the switch would work.
   */
  getPushPublicKey(): Promise<string>
  /** Every browser subscribed to this owner, live and disabled, newest first. Never the keys. */
  getPushSubscriptions(): Promise<PushDevice[]>
  /** Subscribe this browser. Idempotent on the endpoint, and re-enables a row a 410 had killed. */
  subscribeToPush(input: PushSubscribeInput): Promise<PushDevice>
  /** The owner saying "stop" for one browser — really gone, not merely disabled. */
  unsubscribeFromPush(id: string): Promise<PushDevice>
  /** Push "LifeOS test notification" to every live subscription now, and report what each said. */
  sendTestNotification(): Promise<PushTestResult>
  /**
   * The log, newest first. THIS IS THE PRODUCT RULE APPLIED: a notification is a banner that
   * disappears, so without a readable log "did LifeOS tell me about the 9am?" has no answer.
   */
  getNotifications(filter?: NotificationFilter): Promise<NotificationRecord[]>
  /** Leads and per-kind switches. Answers DEFAULTS when nothing has ever been saved. */
  getNotificationSettings(): Promise<NotificationSettings>
  /** Partial. `kindsEnabled` is merged key by key; the whole settings object comes back. */
  updateNotificationSettings(patch: NotificationSettingsPatch): Promise<NotificationSettings>

  // ── mail · Google accounts for read-only Gmail (Day page, Lane 1a) ────────
  //
  // CONNECTING IS A REDIRECT, NOT A FETCH. `startGoogleMailConnect` answers a URL; the browser
  // goes there, Google sends it to the Worker's callback, and the Worker sends it back to
  // /settings/connections with `?mail=connected|error`. Reading mail is the Worker's 30-minute
  // sync (Lane 1b); "Sync now" on the Gmail card is `syncConnector('gmail')`.

  /** The connected Google accounts, oldest first. Never their tokens. */
  listMailAccounts(): Promise<MailAccount[]>
  /**
   * Where to send the browser to connect one more account. Throws the Worker's own sentence when
   * Google sign-in is not set up on the deployment (it names the missing variables). In demo mode
   * the URL is an in-app path, because there is no Google behind a fixture.
   */
  startGoogleMailConnect(): Promise<string>
  /** Forget one account: the Worker asks Google to revoke the grant, then deletes the row. */
  disconnectMailAccount(id: string): Promise<MailDisconnectResult>
  /** What the mail sync has read, newest first, one keyset page at a time (Lane 1b). */
  listMailMessages(filter?: MailMessageFilter): Promise<MailMessagePage>

  // ── mail · triage, grading and rules (Day page prototype, Lane 2 contract) ──
  //
  // The Day page's mail half. `GET /api/mail/triage` answers the open mail already grouped into
  // the four piles plus "not sorted yet", with true counts. Every write answers the updated item
  // (or nothing, for rules); the page re-reads the triage afterwards rather than patching it.

  /**
   * The open mail, grouped by verdict, with each account's freshness. `days` (the designed Day
   * page sends 7) limits the folded piles to that many days back. `scenario` is DEMO-ONLY, the
   * same convention as `getConnectors(scenario)`: it selects one of the Day page board states the
   * demo dataset can show, and remote mode ignores it.
   */
  getMailTriage(query?: MailTriageQuery): Promise<MailTriage>
  /** The weekly check: last week's numbers, ten random items from the folded piles, proposed rules. */
  getMailAudit(weekStart: ISODate, scenario?: DayScenario): Promise<MailAudit>
  /** Move one message to a pile. A second call with `reason` records why (optional chip). */
  setMailVerdict(id: string, verdict: MailVerdict, reason?: MailCorrectionReason): Promise<MailTriageItem>
  /** Done / dismissed / snoozed (with an ISO `snoozedUntil`) / back to open. LifeOS-only; never Gmail. */
  setMailState(id: string, state: MailItemState, snoozedUntil?: ISOTimestamp): Promise<MailTriageItem>
  /** The owner's "always <pile> from <sender>" rules. */
  listMailRules(): Promise<MailRule[]>
  createMailRule(input: MailRuleInput): Promise<void>
  deleteMailRule(id: string): Promise<void>
  /** Sort up to `limit` unsorted messages now. */
  runMailTriage(limit?: number): Promise<MailTriageRunResult>

  // ── mail · next steps (Day page, Lane 6) ──
  /** Accept one proposal: the Worker does what it says (a LifeOS block or reminder, done…). */
  acceptMailProposal(id: string): Promise<MailProposalDecision>
  /** Skip one proposal. Nothing is written but the skip. */
  skipMailProposal(id: string): Promise<MailProposalDecision>
  /** Reverse an accept: the created block or reminder is deleted, a mark_done is reverted. */
  undoMailProposal(id: string): Promise<MailProposalDecision>
}

/**
 * Every method name on DataAPI, as runtime data.
 *
 * A TypeScript interface vanishes at runtime, so the unit test that proves demo.ts and remote.ts
 * are complete needs a list it can iterate. The two type aliases below make this list impossible
 * to forget: adding a method to DataAPI without adding it here is a TYPE error, not a silent gap.
 */
export const DATA_API_METHODS = [
  'getClock',
  'getSyncStatus',
  'getJournalEntry',
  'getJournalEntries',
  'saveJournalEntry',
  'getVoiceMemos',
  'uploadVoiceMemo',
  'getJournalStats',
  'getJournalDayContext',
  'getTerm',
  'getCourses',
  'getClassOccurrences',
  'getAssignments',
  'getLectures',
  'getLectureTranscript',
  // phone lecture upload · /record
  'uploadLecture',
  'getCapturedLectures',
  'getCapturedLecture',
  'assignLectureCourse',
  // the durable phone upload queue · REC-1b
  'registerUpload',
  'putUploadBlob',
  'getUploadState',
  // school · 6a/6b/6c
  'getCourse',
  'getAnnouncements',
  'markAnnouncementsRead',
  'getSyllabus',
  'getSyllabusDocument',
  'generateSyllabus',
  'getLectureSummary',
  'getFlashcards',
  'getPracticeQuestions',
  'generateStudySet',
  'getTasks',
  'createTask',
  'toggleTask',
  'updateTask',
  'deleteTask',
  'getReminders',
  // calendar · 11a/11b
  'getCalendarRange',
  'createCalendarBlock',
  'updateCalendarBlock',
  'deleteCalendarBlock',
  'getLearningTracks',
  'getLearningItems',
  'toggleLearningItem',
  'getLearningStreak',
  // learning · 9a/9b
  'getLearningTrackSummaries',
  'getLearningTrackDetail',
  'getLearningResources',
  'exportLearning',
  'getInterestSaves',
  'getInterestTopics',
  'getInterestsOverview',
  'getInterestSave',
  // connections
  'getConnectors',
  'getDesktopWorker',
  'getCaptureTokens',
  'createCaptureToken',
  'getDataStatus',
  'syncConnector',
  // search · 10a/10b
  'search',
  // chat (11a/11b) and the ⌘K capture palette (11c)
  'getChatThreads',
  'getChatThread',
  'getChatScopes',
  'getChatStarters',
  // the two writes on the Chat screen — asking (V4) and removing a thread
  'sendChatMessage',
  'deleteChatThread',
  // Chat v3 — sending a picture, reading one back, and deciding on what an answer proposed
  'uploadChatAttachment',
  'getChatAttachmentUrl',
  'acceptChatProposal',
  'rejectChatProposal',
  'getClipPreview',
  'captureQuick',
  // notifications · Web Push (NOTIF-1a/1b)
  'getPushPublicKey',
  'getPushSubscriptions',
  'subscribeToPush',
  'unsubscribeFromPush',
  'sendTestNotification',
  'getNotifications',
  'getNotificationSettings',
  'updateNotificationSettings',
  // mail · Google accounts for read-only Gmail (Day page, Lane 1a)
  'listMailAccounts',
  'startGoogleMailConnect',
  'disconnectMailAccount',
  // mail · what the sync read (Lane 1b)
  'listMailMessages',
  // mail · triage, grading and rules (Day page prototype)
  'getMailTriage',
  'setMailVerdict',
  'setMailState',
  'listMailRules',
  'createMailRule',
  'deleteMailRule',
  'runMailTriage',
  // mail · the designed Day page (Lane 4a)
  'getMailAudit',
  // mail · next steps (Lane 6)
  'acceptMailProposal',
  'skipMailProposal',
  'undoMailProposal',
] as const

export type DataAPIMethod = Exclude<keyof DataAPI, 'isDemo'>

// Compile-time completeness, both directions. If either alias is not `never`, tsc fails and
// names exactly which method is missing or misspelled.
type _MissingFromList = Exclude<DataAPIMethod, (typeof DATA_API_METHODS)[number]>
type _ExtraInList = Exclude<(typeof DATA_API_METHODS)[number], DataAPIMethod>
type _AssertNever<T extends never> = T
export type _DataApiListIsComplete = [_AssertNever<_MissingFromList>, _AssertNever<_ExtraInList>]

// ════════════════════════════════════════════════════════════════════════════
// SEARCH — docs/design/handoff/"LifeOS Search.dc.html", options 10a and 10b.
//
// APPENDED as its own block rather than filed into the sections above: several
// screen ports run in parallel and a shared region edited by all of them merges
// badly. Nothing above this line was reorganised.
//
// THE ONE STRUCTURAL RULE THIS BLOCK EXISTS TO ENFORCE. README §Interactions →
// Search: "Literal matches and semantically related items are ranked and
// rendered in separate blocks with explicit headings; never interleave them."
// So they are two fields, not one list with a flag — a single list would let a
// careless `.sort()` mix a guess into the answers, and the whole point of 10b is
// that a guess is labelled as one.
//
// AND `relatedBasis` SAYS WHERE THE SECOND BLOCK CAME FROM. A related item is a
// claim about meaning, and the app is not allowed to imply it computed one when
// it did not (CLAUDE.md → Product identity rules: no invented thresholds). Demo
// mode cannot run an embedding, so it answers 'curated' for the two queries a
// human wrote a neighbour list for and 'unavailable' for everything else — and
// the screen prints that difference instead of silently showing nothing.
// ════════════════════════════════════════════════════════════════════════════

/** The five domains one search covers. These are also the filter chips, in this order. */
export type SearchDomain = 'journal' | 'school' | 'interests' | 'tasks' | 'learning'

export const SEARCH_DOMAINS: readonly SearchDomain[] = [
  'journal',
  'school',
  'interests',
  'tasks',
  'learning',
] as const

/**
 * WHAT kind of thing matched. It picks the glyph and the badge text, and it is stored rather than
 * inferred from `domain`: 'SCHOOL · TRANSCRIPT' and 'SCHOOL · ANNOUNCEMENT' are the same domain
 * and are not the same thing to a reader scanning a mixed list.
 *
 * The four `save_*` kinds are one shape split by SOURCE for the same reason — the mockup's badges
 * are 'INTERESTS · REDDIT', 'INTERESTS · YOUTUBE', 'INTERESTS · ARTICLE', each with its own glyph.
 * Splitting the kind keeps the badge derivable from one field instead of parsing it back out of a
 * locator string.
 */
export type SearchHitKind =
  | 'journal_entry'
  | 'voice_memo'
  | 'transcript'
  | 'announcement'
  | 'syllabus'
  | 'assignment'
  | 'lecture_summary'
  | 'course_file'
  | 'save_reddit'
  | 'save_youtube'
  | 'save_article'
  | 'save_x'
  | 'task'
  | 'learning_item'
  | 'learning_session'

/** One literal match. Every string here is the item's own; the view highlights the terms in place. */
export interface SearchHit {
  id: string
  domain: SearchDomain
  kind: SearchHitKind
  /** What the row calls the thing it found: 'NEU C61 · Lecture 4 · 12:31', 'r/StableDiffusion'. */
  locator: string
  /**
   * The item's own headline, where it has one distinct from its body (a task, a save, an
   * announcement). Null when the body IS the item — a transcript segment has no title, and an
   * invented one would be a summary nobody asked for.
   */
  title: string | null
  /**
   * The matched window of the item's text, with '…' where it was cut. Null when the item has no
   * body at all (a task is its title). Never a summary: this is the item's words, trimmed.
   */
  snippet: string | null
  /** The day the item belongs to. Null when the item has no date — never invented. */
  date: ISODate | null
  /** Where the row goes. An in-app path, always: a hit you cannot open is a hit you cannot use. */
  href: string
  /**
   * Lecture hits only: the matched segment's start. It is what makes the row say 'Jump to 12:31'
   * and what the deep link carries, per CLAUDE.md's citation convention. Null everywhere else.
   */
  timestampMs: number | null
  /**
   * The quiet third line, where there is something true to say: 'audio on desktop · transcript
   * available', 'extracted from PDF', 'due Sep 8'. Null when there is not.
   */
  note: string | null
  /** The item's own tags — the journal row's `school` / `habits` chips. Empty, never null. */
  chips: string[]
  /**
   * Matches inside the same item that this snippet does not show ('2 other matches in this
   * entry'). Zero when the snippet covers them all; never a guess.
   */
  otherMatches: number
  /** Relevance. Higher first. Ranked across all domains — never grouped by type. */
  score: number
}

/**
 * ONE SEMANTIC NEIGHBOUR. Deliberately a smaller shape than SearchHit: it carries no snippet and
 * no highlight, because there is nothing in it to highlight — that is what "no literal match"
 * means, and a related row that looked like an answer row would be the interleaving the handoff
 * forbids, done with CSS instead of with an array.
 */
export interface SearchRelatedItem {
  id: string
  domain: SearchDomain
  kind: SearchHitKind
  locator: string
  title: string
  date: ISODate | null
  href: string
  /** A right-aligned fact where the item has one: '18 min'. Null when it has none. */
  badge: string | null
}

/**
 * Where the related block's contents came from.
 *
 *   'curated'     — a person wrote this neighbour list. Honest, and labelled as such in the UI.
 *   'unavailable' — nothing could be computed, so nothing is claimed. The block says which
 *                   version brings real neighbours rather than rendering empty.
 *   'vector'      — Cloudflare Vectorize actually answered (V4, remote mode only). An EMPTY
 *                   related block under this basis is a real answer — "nothing near this" — and
 *                   the screen prints a different sentence for it than for 'unavailable'.
 */
export type SearchRelatedBasis = 'curated' | 'unavailable' | 'vector'

export interface SearchResults {
  /** Echoed back so a late response can be discarded rather than rendered under a newer query. */
  query: string
  /** The terms the query was split into — what the view highlights, lowercased. */
  terms: string[]
  /** The literal block, ranked by relevance across every domain, never grouped by type. */
  exact: SearchHit[]
  /** The separate lower block. Never interleaved with `exact`; may be empty. */
  related: SearchRelatedItem[]
  relatedBasis: SearchRelatedBasis
  /**
   * Per-domain counts over BOTH blocks — the filter chips' numbers, and the chips are a lens on
   * the whole page. Every domain is present, including the ones at zero: "School 0" is
   * information (the app looked there), and a chip that vanishes hides where else the answer was.
   */
  counts: Record<SearchDomain, number>
}

// ════════════════════════════════════════════════════════════════════════════
// ASKING — V4, the one chat engine (apps/worker/src/routes/chat.ts).
//
// APPENDED as its own block rather than filed into the CHAT section above, for
// the same reason the SEARCH block is appended: several lanes edit this file at
// once and a shared region touched by all of them merges badly.
//
// THE MODE IS THE COST POLICY, not decoration. The composer's `quick` / `deep`
// segmented control was drawn in the handoff and, until V4, changed only what the
// explainer line said. It now picks the model tier: `quick` is Haiku, which is
// what per-item work runs on; `deep` is the Sonnet tier and is the owner opting
// in, per question (CLAUDE.md → AI cost policy).
// ════════════════════════════════════════════════════════════════════════════

/** Which model tier answers. `quick` is the default and always will be. */
export type ChatTier = 'quick' | 'deep'

export interface ChatSendInput {
  /** Null starts a thread. The title is derived from the question, never generated. */
  threadId: string | null
  text: string
  /**
   * The scope to ask at. On an existing thread this RE-SCOPES it from this message on — answers
   * already given keep the sources they actually read (README §Interactions → Chat).
   */
  scopeId: ChatScopeId
  tier: ChatTier
  /** Pictures already uploaded with `uploadChatAttachment`. At most four. */
  attachmentIds?: string[]
}

export interface ChatSendResult {
  thread: ChatThread
  /** The question and the answer, in that order. */
  messages: ChatMessage[]
  /**
   * WHAT WAS DEGRADED, in words, or null — a tier that could not be served, a search index that
   * was not bound. Honest degradation is designed, not improvised (CLAUDE.md → Design system), and
   * this is the field that carries it to the screen instead of leaving it in a log.
   */
  note: string | null
}
// ═══════════════════════════════════════════════════════════════════════════
// CHAT v3 — sending a picture, and being OFFERED something rather than having it done.
//
// APPENDED as its own block, the same reason the SEARCH and ASKING blocks above are: several
// lanes edit this file at once and a shared region touched by all of them merges badly.
//
// THE PRODUCT RULE THIS SHAPE EXISTS TO CARRY: **propose, never silently do.** The owner pastes a
// screenshot — a Calendly confirmation, a flyer — and the answer comes back with a CARD saying
// "here is the calendar block I think this is". Nothing exists until he presses Accept. That is
// not a UI nicety: an app that writes to the owner's calendar on a model's say-so is an app whose
// mistakes he finds later, alone, in a week that has an appointment he never made.
//
// AND EVERY PROPOSAL CITES. `evidence` is never empty — a proposal that could not say where it
// came from was dropped before it reached the browser, and the message's `note` says how many
// went. It is the same invariant `ChatMessage.sources` carries, applied to the write side.
// ═══════════════════════════════════════════════════════════════════════════

/** The three types a browser produces from a paste, a drop, or a file picker. */
export type ChatAttachmentMime = 'image/png' | 'image/jpeg' | 'image/webp'

export interface ChatAttachment {
  id: string
  mime: ChatAttachmentMime
  /** How big the stored file is. Not an estimate. */
  bytes: number
}

/**
 * WHERE A PROPOSAL CAME FROM — resolved, not the model's claim about it.
 *
 * A `source` entry carries the whole numbered card, so the evidence line renders with the same
 * component the answer's own citations do. An `attachment` entry carries an id the browser can
 * load the thumbnail from. A `request` entry carries the owner's OWN words — the Worker checked
 * that the quote really is a piece of the message he sent before storing it, which is what lets
 * the card say "You asked" without that being a sentence a model wrote.
 *
 * `speaker` is present only on a transcript citation from a DIARIZED recording: `professor` (the
 * voice that talked the most — a likelihood, and the card says so) or `speaker-2`, `speaker-3`, …
 * Absent means nobody worked out who was speaking, and the card then shows nothing at all —
 * never "unknown speaker".
 */
export type ChatProposalEvidence =
  | { kind: 'source'; cite: number; source: ChatSource; speaker?: string | null }
  | { kind: 'attachment'; attachmentId: string; mime: ChatAttachmentMime }
  | { kind: 'request'; quote: string }

/** Something already in the week that this proposal would land on top of. */
export interface ChatProposalConflict {
  kind: 'calendar_block' | 'google_event' | 'assignment'
  title: string
  /** 'Thu Sep 3 · 1:00 PM' — already in 12-hour local time. The screen never formats an instant. */
  whenLabel: string
}

export interface ChatCalendarBlockProposal {
  kind: 'calendar_block'
  title: string
  startsAt: ISOTimestamp
  endsAt: ISOTimestamp
  /** The zone the wall clock was read in. Shown so the card can say what it assumed. */
  timezone: string
  location: string | null
  /** Which lane of the week it would go in. NOT where it came from — that is `proposalId`. */
  lane: 'personal' | 'work' | 'learning' | 'task'
  notes: string | null
  /** The literal text this was read from. The one thing that makes the card checkable by eye. */
  quote: string
}

export interface ChatTaskProposal {
  kind: 'task'
  title: string
  /** A calendar DAY, or null. */
  due: string | null
  /** An INSTANT, or null. Independent of `due`: a reminder can have either, both or neither. */
  reminderAt: ISOTimestamp | null
  list: string | null
  notes: string | null
  quote: string
  /**
   * 'FREQ=DAILY' | 'FREQ=WEEKLY' | 'FREQ=MONTHLY' | null. Absent on proposals stored before
   * 2026-09-17. Materialise-on-complete, like every task rrule — the card says so.
   */
  rrule?: string | null
}

export type ChatProposalPayload = ChatCalendarBlockProposal | ChatTaskProposal

export interface ChatProposal {
  id: string
  kind: 'calendar_block' | 'task'
  /**
   * `proposed` — the card is on screen and nothing exists. `accepted` — `resultId` names the row
   * it made. `rejected` — nothing was written and nothing will be. There is no fourth state.
   */
  status: 'proposed' | 'accepted' | 'rejected'
  payload: ChatProposalPayload
  /** NEVER EMPTY. A proposal with nothing behind it was dropped before it got here. */
  evidence: ChatProposalEvidence[]
  /**
   * What it would collide with, found WHEN THE PROPOSAL WAS MADE and frozen. An empty array means
   * the app looked and found nothing — which is a different sentence from "the app did not look".
   */
  conflicts: ChatProposalConflict[]
  resultId: string | null
  decidedAt: ISOTimestamp | null
  createdAt: ISOTimestamp
}

/** What accepting created, in the words the card reports back. */
export interface ChatProposalResult {
  kind: 'calendar_block' | 'task'
  id: string
  title: string
  /** Where it is in the app — the calendar day it landed on, or the Reminders board. */
  href: string
  /** 'Thu Sep 3 · 1:00 PM', or null for a reminder with no time. */
  whenLabel: string | null
}

export interface ChatProposalDecision {
  proposal: ChatProposal
  created: ChatProposalResult
}

// ════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS — Web Push (NOTIF-1a on the Worker, NOTIF-1b here).
//
// APPENDED as its own block, the convention this file already keeps for a late
// domain: a shared region edited by several lanes at once merges badly.
//
// EVERY SHAPE BELOW IS THE WORKER'S, NOT A NEW ONE. They are transcribed from
// `PushSubscriptionSchema`, `NotificationSchema` and `NotificationSettingsSchema`
// in apps/worker/src/lib/notifications.ts, field for field, because the whole
// point of the machine-readability rule is that the screen and an LLM asking the
// same endpoint get the same answer. Nothing here is computed in the browser.
//
// ONE RENAME, AND IT IS DELIBERATE: the row the API calls a "push subscription"
// is a `PushDevice` here. `PushSubscription` is a DOM interface — the object
// `pushManager.subscribe()` hands back — and a file that has to hold both the
// browser's object and the server's row cannot spell them the same way without
// one of them silently winning. The wire name is untouched; only this type's
// name differs, and lib/push.ts is where the two meet.
// ════════════════════════════════════════════════════════════════════════════

/** The five kinds a notification row can be. `test` is only ever made by the test button. */
export type NotificationKind =
  | 'task_reminder'
  | 'calendar_block'
  | 'gcal_event'
  | 'class_start'
  | 'test'

/** The four the scheduler produces — and the four with an off switch on the settings screen. */
export type ScheduledNotificationKind = Exclude<NotificationKind, 'test'>

export const SCHEDULED_NOTIFICATION_KINDS: readonly ScheduledNotificationKind[] = [
  'task_reminder',
  'calendar_block',
  'gcal_event',
  'class_start',
] as const

/**
 * `queued` decided but not yet delivered · `sent` at least one browser accepted it · `failed`
 * every subscription refused it · `skipped` deliberately not sent, with the reason in `error`.
 */
export type NotificationStatus = 'queued' | 'sent' | 'failed' | 'skipped'

/**
 * ONE SUBSCRIBED BROWSER. The encryption keys are NOT here and no endpoint returns them — they go
 * in once and are read only by the Worker's encryptor (rule 1 at the top of the Worker's
 * routes/notifications.ts).
 */
export interface PushDevice {
  id: string
  /** The push service's URL for this browser. A bearer capability — treat it like a token. */
  endpoint: string
  /** What the owner called it, or null. Never invented from the user agent. */
  label: string | null
  userAgent: string | null
  createdAt: ISOTimestamp
  /** When a push was last ACCEPTED. Null means none ever was — which a new device honestly is. */
  lastSuccessAt: ISOTimestamp | null
  /** Set when the push service said this subscription is gone (404/410). The row is kept. */
  disabledAt: ISOTimestamp | null
  disabledReason: string | null
}

/** What the browser's own `PushSubscription.toJSON()` gives, plus what only the owner knows. */
export interface PushSubscribeInput {
  endpoint: string
  keys: { p256dh: string; auth: string }
  /** Absent means none — the server does not guess one from the user agent either. */
  label?: string
  userAgent?: string
}

/** What one subscription said when the test notification was pushed to it. */
export interface PushTestOutcome {
  subscriptionId: string
  label: string | null
  ok: boolean
  /** Null when it was accepted. */
  error: string | null
  /** True when the push service said this subscription is gone, so it was disabled. */
  disabled: boolean
}

export interface PushTestResult {
  /** The `test` row this send was logged as — readable back through the log. */
  notificationId: string | null
  sent: number
  /** Empty means nothing is subscribed, which is a different answer from "everything refused". */
  outcomes: PushTestOutcome[]
}

/** One row of the log: what LifeOS decided to say, and what happened to it. */
export interface NotificationRecord {
  id: string
  kind: NotificationKind
  /** What it is about in the source domain's terms — a task id, a block id, a course code. */
  sourceId: string
  /** The instant it was FOR, not when it was sent. */
  fireAt: ISOTimestamp
  title: string
  body: string | null
  /** Where tapping it goes, as an in-app path. */
  url: string
  /** The idempotency key — what makes one reminder found on four cron runs one notification. */
  dedupeKey: string
  status: NotificationStatus
  sentAt: ISOTimestamp | null
  /** Why a `failed` or `skipped` row is what it is. Plain words, never a trace. */
  error: string | null
  attempts: number
  createdAt: ISOTimestamp
}

export interface NotificationFilter {
  /** An ISO instant. Only notifications created at or after it. */
  since?: string
  status?: NotificationStatus
  limit?: number
}

export interface NotificationSettings {
  /** How long before a class meeting the "tap to record" notification goes out. */
  classLeadMinutes: number
  /** ONE lead for every mirrored Google event — Google's own reminders do not ride an ICS feed. */
  gcalLeadMinutes: number
  kindsEnabled: Record<ScheduledNotificationKind, boolean>
  /**
   * THE SCHEDULER HEARTBEAT (OPS-1a): when the per-minute notification cron last FINISHED a run,
   * including the runs that found nothing. `null` means it has never run. OPTIONAL because a
   * Worker from before the heartbeat does not send it, and an absent field must stay absent on
   * the screen rather than be read as "never".
   */
  schedulerLastRunAt?: ISOTimestamp | null
  /** What that run did, in counts. `null` when it has never run. */
  schedulerLastResult?: SchedulerRunResult | null
  /**
   * THE DAY PAGE'S ONE DAILY NOTIFICATION ("N need you today"), local time. Optional: a Worker
   * from before the designed Day page does not send it, and then the screen draws no switch for
   * it rather than one with nothing behind it.
   */
  dayDigest?: DayDigestSetting
}

export interface DayDigestSetting {
  enabled: boolean
  /** 0–23, local. */
  hour: number
  /** 0–59. */
  minute: number
}

/** The Worker's `SchedulerRunResultSchema`, field for field. */
export interface SchedulerRunResult {
  /** Candidates found in the window plus rows carried over still queued. */
  considered: number
  sent: number
  failed: number
  skipped: number
  /** Subrequests the run had left when it finished. */
  budgetLeft: number
  /** Present only when the run could not finish. A sentence, never a trace. */
  error?: string
}

/** PARTIAL: anything omitted keeps its value, and `kindsEnabled` is merged key by key. */
export interface NotificationSettingsPatch {
  classLeadMinutes?: number
  gcalLeadMinutes?: number
  kindsEnabled?: Partial<Record<ScheduledNotificationKind, boolean>>
  /** Sent whole — the three fields are one setting. */
  dayDigest?: DayDigestSetting
}

// ════════════════════════════════════════════════════════════════════════════
// MAIL — Google accounts for read-only Gmail (Day page, Lane 1a).
//
// APPENDED as its own block, the convention this file keeps for a late domain.
// Transcribed from the Worker's `MailAccountSchema` (apps/worker/src/lib/
// mail-accounts.ts) field for field. The refresh token is not here because no
// endpoint returns it.
// ════════════════════════════════════════════════════════════════════════════

export interface MailAccount {
  id: string
  /** The address, lower-cased, as Google reported it when it connected. */
  address: string
  connectedAt: ISOTimestamp
  /** Null until the mail sync has run — absent, not zero. */
  lastSyncAt: ISOTimestamp | null
  /** What the last attempt to use this account said went wrong. Plain words. */
  lastError: string | null
  /** `error` exactly when `lastError` is set. */
  status: 'connected' | 'error'
  /** Messages LifeOS holds from this inbox. Null when it could not be counted — unknown, not zero. */
  messageCount: number | null
}

export interface MailDisconnectResult {
  deleted: true
  /** Whether Google confirmed the revoke. The row is deleted either way. */
  revoked: boolean
}

// ── Messages (Lane 1b) — transcribed from the Worker's `MailMessageSchema` (routes/mail.ts) ──
//
// HEADERS AND GMAIL'S SNIPPET, NEVER A BODY: LifeOS does not store message bodies (owner decision,
// 2026-10-02), so there is no field here that could hold one.

export interface MailMessage {
  id: string
  accountId: string
  /** Which connected inbox it is in. Null only if that account vanished mid-read. */
  accountAddress: string | null
  /** Gmail's message id. */
  externalId: string
  threadId: string
  /** `name` is null when the From header carried an address only. */
  from: { name: string | null; address: string }
  to: string[]
  /** Empty string when the message has no subject. */
  subject: string
  /** When Gmail received it. */
  receivedAt: ISOTimestamp
  /** Gmail's own ~200-character preview. */
  snippet: string
  labelIds: string[]
  /** Unread at the last sync — not live. */
  isUnread: boolean
  /** Opens the thread in Gmail, in the right inbox. */
  url: string
}

/** GET /api/mail/messages's filters. Every one optional; newest first either way. */
export interface MailMessageFilter {
  /** One account id. */
  account?: string
  /** ISO timestamp, inclusive, on `receivedAt`. */
  since?: ISOTimestamp
  /** 1–200; the Worker's default is 50. */
  limit?: number
  /** The previous page's `nextCursor`. */
  cursor?: string
}

export interface MailMessagePage {
  messages: MailMessage[]
  /** Null on the last page. */
  nextCursor: string | null
}

// ── Triage (Day page prototype) — the Worker's `/api/mail/triage` contract, field for field ──
//
// A verdict is one of four piles. `verdict` is what the page files the message under: the owner's
// pile when he moved it, else the model's or a rule's. `modelVerdict` stays as the model said it,
// so the page can show both ("sorted as noise · you moved it") — corrections never overwrite it.

export type MailVerdict = 'needs_you_today' | 'important' | 'can_wait' | 'noise'
export type MailItemState = 'open' | 'done' | 'snoozed' | 'dismissed'
export type MailAction = 'reply' | 'pay' | 'submit' | 'read' | 'schedule' | 'none'
/** The optional chip after a move. */
export type MailCorrectionReason = 'person' | 'deadline' | 'money' | 'not_interested' | 'other'

export interface MailTriageItem {
  id: string
  accountId: string
  accountAddress: string
  from: { name: string | null; address: string }
  subject: string
  snippet: string
  receivedAt: ISOTimestamp
  /** Opens the thread in Gmail. */
  url: string
  /** Null = not sorted yet. */
  verdict: MailVerdict | null
  modelVerdict: MailVerdict | null
  ownerVerdict: MailVerdict | null
  /** One sentence: why it is in this pile. */
  reason: string | null
  /** Only when the mail states one. */
  deadline: ISODate | null
  action: MailAction | null
  security: boolean
  isCode: boolean
  state: MailItemState
  snoozedUntil: ISOTimestamp | null
  firstSurfacedOn: ISODate | null
  /** Days since it first surfaced; > 0 means carried over. */
  ageDays: number
  triagedAt: ISOTimestamp | null
  triageModel: string | null
  triageSource: 'model' | 'rule' | null
  /** What the Worker proposes as next steps (Lane 6), newest first, every status. */
  proposals: MailProposal[]
  /** When the Worker last proposed for this message. Null = it has not run for it yet. */
  proposalsAt: ISOTimestamp | null
}

// ── Next steps (Day page, Lane 6) — the Worker's `/api/mail/proposals` contract, field for field ──
//
// Computed AHEAD of time in the sync cron, for the two top piles. The body is read for the call and
// never stored (`source: 'body'`); without it the proposal came from the subject and snippet. Nothing
// is applied until the owner accepts, and every accept writes to LifeOS only — never Gmail, never
// Google Calendar.

export type MailProposalKind =
  | 'calendar_block'
  | 'reminder'
  | 'open_link'
  | 'mark_done'
  | 'waiting_on'
  | 'draft_reply'
  | 'verify_official'
  | 'already_in_lifeos'
export type MailProposalStatus = 'proposed' | 'accepted' | 'skipped'

export interface MailProposal {
  id: string
  messageId: string
  kind: MailProposalKind
  title: string
  /** One factual sentence of evidence. Always shown. */
  detail: string
  payload:
    | { title: string; startAt: string; endAt: string; location?: string } // calendar_block
    | { title: string; dueDate: string } // reminder, waiting_on
    | { url: string; label: string } // open_link
    | { reason: string } // mark_done
    | { text: string } // draft_reply
    | { step: string } // verify_official
    | { what: 'calendar_block' | 'task' | 'assignment'; id: string; title: string } // already_in_lifeos
  status: MailProposalStatus
  source: 'body' | 'snippet'
  createdAt: string
  model: string
  promptVersion: string
  acceptedAt: string | null
  skippedAt: string | null
  result: { kind: 'calendar_block' | 'task'; id: string } | null
}

/** What `POST /api/mail/proposals/{id}/accept|skip|undo` answers. */
export interface MailProposalDecision {
  proposal: MailProposal
  item: MailTriageItem
}

export interface MailTriageAccount {
  id: string
  address: string
  lastSyncAt: ISOTimestamp | null
  lastError: string | null
}

export interface MailTriageCounts {
  needsYouToday: number
  important: number
  canWait: number
  noise: number
  unsorted: number
  /** Sign-in codes past their use, hidden rather than listed. */
  expiredCodes: number
  /**
   * Snoozed and not back yet. OPTIONAL on the type because a Worker from before the designed Day
   * page (Lane 4) does not send it; the page treats absent as "not known", never as zero.
   */
  snoozed?: number
  /** Marked done or dismissed today, in the owner's day. Same optionality as `snoozed`. */
  handledToday?: number
}

export interface MailTriageGroups {
  needsYouToday: MailTriageItem[]
  important: MailTriageItem[]
  canWait: MailTriageItem[]
  noise: MailTriageItem[]
  unsorted: MailTriageItem[]
  /** Snoozed and not back yet, soonest back first. Optional for the reason `counts.snoozed` is. */
  snoozed?: MailTriageItem[]
}

export interface MailTriage {
  generatedAt: ISOTimestamp
  accounts: MailTriageAccount[]
  counts: MailTriageCounts
  groups: MailTriageGroups
  /** When the soonest snoozed message comes back. Null when nothing is snoozed. */
  nextSnoozedAt?: ISOTimestamp | null
}

/**
 * THE DAY PAGE BOARD STATES THE DEMO DATASET CAN SHOW (docs/design/handoff/day-page/README.md →
 * Boards). Demo-only, like `ConnectionsScenario`: `/day?state=<one>` selects it in demo mode so the
 * FIDELITY side-by-side proof can photograph every state without an account. Remote ignores it.
 */
export type DayScenario = 'morning' | 'quiet' | 'degraded' | 'first-run' | 'loading'

export interface MailTriageQuery {
  /** Fold window for the piles, in days. The designed page sends 7 ("piles show the last 7 days"). */
  days?: number
  /** Demo-only. See `DayScenario`. */
  scenario?: DayScenario
}

// ── The weekly check (Day page, Lane 4a) — `GET /api/mail/audit?weekStart=YYYY-MM-DD` ──
//
// THERE IS NO "REPLIED TO" FIGURE. The board draws "3 you replied to from the noise pile"; LifeOS
// does not read sent mail, so the API carries no such number and the page does not draw one.

export interface MailAuditProposedRule {
  matchType: 'address' | 'domain'
  matchValue: string
  verdict: MailVerdict
  /** One sentence, the model's own, saying what this week's moves showed. */
  evidence: string
  example: { subject: string; fromAddress: string }
}

export interface MailAudit {
  weekStart: ISODate
  weekEnd: ISODate
  generatedAt: ISOTimestamp
  counts: {
    sorted: number
    moved: number
    movedByPile: Record<MailVerdict, number>
    dismissed: number
    done: number
  }
  /** At most ten, at random from the folded piles. */
  sample: MailTriageItem[]
  proposedRules: MailAuditProposedRule[]
}

export interface MailRuleInput {
  matchType: 'address' | 'domain'
  matchValue: string
  verdict: MailVerdict
}

export interface MailRule extends MailRuleInput {
  id: string
  createdAt: ISOTimestamp
  /** Open messages this rule files right now. Optional: a Worker before Lane 4 does not send it. */
  messageCountOpen?: number
}

export interface MailTriageRunResult {
  sorted: number
  failed: number
  remaining: number
}
